'use client';

import { useMemo, useState } from 'react';

import Link from 'next/link';
import {
  Home,
  LogOut,
  MessageCircleQuestion,
  Shield,
  User,
} from 'lucide-react';

import { JWTUserData } from '@kit/supabase/types';
import { Avatar, AvatarFallback, AvatarImage } from '@kit/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@kit/ui/dropdown-menu';
import { If } from '@kit/ui/if';
import { InlineModeToggle } from '@kit/ui/mode-toggle';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { usePersonalAccountData } from '../hooks/use-personal-account-data';

export function PersonalAccountDropdown({
  className,
  user,
  signOutRequested,
  paths,
  features,
  account,
}: {
  user: JWTUserData;

  account?: {
    id: string | null;
    name: string | null;
    picture_url: string | null;
    clientName?: string | null;
  };

  signOutRequested: () => unknown;

  paths: {
    home: string;
    myAccount?: string;
  };

  features: {
    enableThemeToggle: boolean;
  };

  showProfileName?: boolean;

  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const { data: personalAccountData } = usePersonalAccountData(
    user.id,
    account,
  );

  const signedInAsLabel = useMemo(() => {
    // Always use the most current email from user data
    const email = user?.email ?? undefined;
    const phone = user?.phone ?? undefined;

    return email ?? phone;
  }, [user?.email, user?.phone]);

  // Priority: clientName from personalAccountData (includes full name), then fallback to email
  const displayName = personalAccountData?.clientName ?? user?.email ?? '';

  const isSuperAdmin = useMemo(() => {
    const hasAdminRole = user?.app_metadata.role === 'super-admin';
    const isAal2 = user?.aal === 'aal2';

    return hasAdminRole && isAal2;
  }, [user]);

  // Get the picture URL - prefer personalAccountData (from users table) over account (workspace)
  const pictureUrl = personalAccountData?.picture_url ?? account?.picture_url;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      {/* Avatar-only trigger */}
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-test={'account-dropdown-trigger'}
          className={cn(
            'flex items-center cursor-pointer',
            className ?? '',
          )}
          aria-label="Open your profile menu"
        >
          <Avatar className="rounded-full h-6 w-6 border border-border">
            <AvatarImage src={pictureUrl ?? undefined} />
            <AvatarFallback className="rounded-full text-xs">
              {displayName
                ? displayName.trim().split(/\s+/).map(p => p[0]).join('').slice(0, 2).toUpperCase()
                : user?.email?.[0]?.toUpperCase() ?? 'U'}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className={'xl:min-w-[15rem]!'}>
        <DropdownMenuItem className={'h-10! rounded-none'}>
          <div
            className={'flex flex-col justify-start truncate text-left text-xs'}
          >
            <div className={'text-muted-foreground'}>
              <Trans i18nKey={'common:signedInAs'} />
            </div>

            <div>
              <span className={'block truncate'}>{signedInAsLabel}</span>
            </div>
          </div>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem asChild>
          <Link
            className={'s-full flex cursor-pointer items-center space-x-2'}
            href={paths.home}
          >
            <Home className={'h-5'} />

            <span>
              <Trans i18nKey={'common:routes.home'} />
            </span>
          </Link>
        </DropdownMenuItem>

        <If condition={!!paths.myAccount}>
          <DropdownMenuItem asChild>
            <Link
              className={'s-full flex cursor-pointer items-center space-x-2'}
              href={paths.myAccount!}
            >
              <User className={'h-5'} />

              <span>
                <Trans i18nKey={'common:myAccount'} defaults={'My Account'} />
              </span>
            </Link>
          </DropdownMenuItem>
        </If>

        <DropdownMenuItem asChild>
          <a
            className={'s-full flex cursor-pointer items-center space-x-2'}
            href={'https://docs.leagen.co'}
            target="_blank"
            rel="noopener noreferrer"
          >
            <MessageCircleQuestion className={'h-5'} />

            <span>
              <Trans i18nKey={'common:documentation'} />
            </span>
          </a>
        </DropdownMenuItem>

        <If condition={isSuperAdmin}>
          <DropdownMenuSeparator />

          <DropdownMenuItem asChild>
            <Link
              className={
                's-full flex cursor-pointer items-center space-x-2 text-yellow-700 dark:text-yellow-500'
              }
              href={'/admin'}
            >
              <Shield className={'h-5'} />

              <span>Super Admin</span>
            </Link>
          </DropdownMenuItem>
        </If>

        <DropdownMenuSeparator />

        <If condition={features.enableThemeToggle}>
          <InlineModeToggle />
        </If>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          data-test={'account-dropdown-sign-out'}
          role={'button'}
          className={'cursor-pointer'}
          onClick={signOutRequested}
        >
          <span className={'flex w-full items-center space-x-2'}>
            <LogOut className={'h-5'} />

            <span>
              <Trans i18nKey={'auth:signOut'} />
            </span>
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
